import { useEffect, useState } from 'react';
import { Dumbbell, Edit, Plus, Search, Users, BarChart2, History } from 'lucide-react';
import { format } from 'date-fns';
import { supabase } from '../services/supabase';
import Modal from '../components/ui/Modal';
import { OpcionesDeEntrenador } from '../components/OpcionesDeEntrenador';
import { ROLES_ASIGNABLES, type EntrenadorAsignable } from '../lib/entrenadoresAsignables';

type TabType = 'exercises' | 'assignments' | 'dashboard' | 'history';

type CreditStatus = 'activo' | 'pendiente' | 'agotado';

interface HistoryCredit {
  id: string;
  user_id: string;
  purchase_date: string;
  activated_at: string | null;
  expiry_date: string | null;
  active: boolean;
  is_gift: boolean;
  user?: { full_name: string; email: string };
  monitor?: { full_name: string } | null;
}

interface RawHistoryCreditRow {
  id: string;
  user_id: string;
  purchase_date: string;
  activated_at: string | null;
  expiry_date: string | null;
  active: boolean;
  is_gift: boolean;
  profiles: { full_name: string; email: string };
}

interface RawMonitorAssignRow {
  user_id: string;
  assigned_at: string;
  active: boolean;
  profiles: { full_name: string };
}

// Shape devuelto por `user_credits.select('user_id, expiry_date,
// profiles!inner(full_name, email)')` para `loadAssignments`.
interface RawAssignmentCreditRow {
  user_id: string;
  expiry_date: string | null;
  profiles: { full_name: string; email: string };
}

// Shape devuelto por `gym_monitor_assignments.select(...,
// profiles!gym_monitor_assignments_monitor_id_fkey(full_name))`.
interface RawCurrentAssignmentRow {
  id: string;
  user_id: string;
  monitor_id: string | null;
  assigned_at: string;
  active: boolean;
  profiles: { full_name: string } | null;
}

function computeStatus(c: HistoryCredit): CreditStatus {
  const expired = c.expiry_date != null && new Date(c.expiry_date) <= new Date();
  if (c.active && c.activated_at == null) return 'pendiente';
  if (!c.active || expired) return 'agotado';
  return 'activo';
}

const STATUS_BADGE: Record<CreditStatus, { label: string; cls: string }> = {
  activo: { label: 'Activo', cls: 'badge-success' },
  pendiente: { label: 'Pendiente de activar', cls: 'badge-warning' },
  agotado: { label: 'Agotado', cls: 'badge-silver' },
};

interface GymExercise {
  id: string;
  name_es: string;
  name_en: string;
  description: string | null;
  category: string;
  material: string;
  difficulty: number;
  measurement_type: string;
  video_url: string | null;
  exercisedb_gif_url: string | null;
  consult_trainer: boolean;
  active: boolean;
}

interface Assignment {
  id: string;
  user_id: string;
  monitor_id: string | null;
  assigned_at: string;
  active: boolean;
  user?: { full_name: string; email: string };
  monitor?: { full_name: string };
  expiry_date?: string | null;
}

interface ExerciseForm {
  name_es: string;
  name_en: string;
  description: string;
  category: string;
  material: string;
  difficulty: number;
  measurement_type: string;
  video_url: string;
  exercisedb_gif_url: string;
  consult_trainer: boolean;
  active: boolean;
}

const CATEGORY_LABELS: Record<string, string> = {
  movilidad: 'Movilidad',
  activacion: 'Activación',
  isometricos: 'Isométricos',
  pliometricos: 'Pliométricos',
  main_lift: 'Main Lift',
  secondary_lift: 'Secondary Lift',
  auxiliares: 'Auxiliares',
  running: 'Running',
};

const MATERIAL_LABELS: Record<string, string> = {
  ninguno: 'Sin material',
  mancuernas: 'Mancuernas',
  barra: 'Barra',
  maquina: 'Máquina',
  bicicleta: 'Bicicleta',
  banda_elastica: 'Banda elástica',
  caja: 'Caja',
  swiss_ball: 'Swiss Ball',
  foam_roller: 'Foam Roller',
  sled: 'Trineo',
  exterior: 'Exterior',
};

const MEASUREMENT_LABELS: Record<string, string> = {
  series_reps_peso: 'Series × Reps × Peso',
  series_reps: 'Series × Reps',
  tiempo: 'Tiempo',
  distancia_tiempo: 'Distancia + Tiempo',
  series_distancia: 'Series × Distancia',
};

const EXERCISE_FORM_EMPTY: ExerciseForm = {
  name_es: '',
  name_en: '',
  description: '',
  category: 'movilidad',
  material: 'ninguno',
  difficulty: 2,
  measurement_type: 'tiempo',
  video_url: '',
  exercisedb_gif_url: '',
  consult_trainer: true,
  active: true,
};

export default function GymPage({ initialTab = 'exercises' }: { initialTab?: TabType }) {
  const [tab, setTab] = useState<TabType>(initialTab);

  // Exercises state
  const [exercises, setExercises] = useState<GymExercise[]>([]);
  const [exSearch, setExSearch] = useState('');
  const [exCategory, setExCategory] = useState('todos');
  const [exLoading, setExLoading] = useState(true);
  const [exModal, setExModal] = useState(false);
  const [exEditTarget, setExEditTarget] = useState<GymExercise | null>(null);
  const [exForm, setExForm] = useState<ExerciseForm>(EXERCISE_FORM_EMPTY);
  const [exSaving, setExSaving] = useState(false);
  // El modal de ejercicio cerraba sin mirar el resultado, y `assignMonitor`
  // desactiva la asignación vieja ANTES de crear la nueva: si el segundo paso
  // falla, el alumno se queda SIN entrenador de gimnasio y nadie se entera.
  const [exModalError, setExModalError] = useState('');
  const [asignError, setAsignError] = useState('');
  // [BUG 9] Estado propio para los fallos de LECTURA, separado de `asignError`,
  // que es el de las escrituras. Compartirlos los pisa: al cambiar de entrenador
  // la página recarga, y una recarga que limpiara `asignError` borraría el aviso
  // de un alta que no entró — el más caro de los dos, porque deja al alumno sin
  // entrenador y sin que nadie se entere. Lo cazó `panel-escrituras-modal`.
  const [cargaError, setCargaError] = useState('');

  // Assignments state
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [monitors, setMonitors] = useState<EntrenadorAsignable[]>([]);
  const [assLoading, setAssLoading] = useState(true);

  // Dashboard state.
  //
  // `null` = no se ha podido contar, y se pinta «—». No es 0: «0 vídeos
  // pendientes de revisión» es un visto bueno —el entrenador no tiene nada que
  // mirar— y «0 alumnos con suscripción de PF activa» es un club que no vende
  // PF. Los tres son la portada de la pestaña.
  //
  // Estos tres sitios estuvieron INVISIBLES para el censo seis lotes: iban en
  // un `Promise.all` destructurando `{ count: … }`, y el guard solo miraba
  // `data`. Se ven desde que se amplió (ver su cabecera).
  const [dashStats, setDashStats] = useState<{
    activeStudents: number | null;
    weekSessions: number | null;
    pendingVideos: number | null;
  }>({
    activeStudents: 0,
    weekSessions: 0,
    pendingVideos: 0,
  });

  // History state
  const [history, setHistory] = useState<HistoryCredit[]>([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [historyFilter, setHistoryFilter] = useState<CreditStatus | 'todos'>('todos');

  useEffect(() => {
    if (tab === 'exercises') loadExercises();
    else if (tab === 'assignments') loadAssignments();
    else if (tab === 'history') loadHistory();
    else loadDashboard();
  }, [tab]);

  async function loadExercises() {
    setExLoading(true);
    const { data, error } = await supabase
      .from('gym_exercises')
      .select('*')
      .order('category')
      .order('name_es');
    // El catálogo vacío se lee como «este club no tiene ejercicios», y desde
    // aquí se crean: alguien lo daría por vacío y volvería a sembrarlo.
    if (error) {
      setCargaError(
        'No se ha podido cargar el catálogo de ejercicios. La lista vacía no significa que no haya ninguno.'
      );
      setExLoading(false);
      return;
    }
    setCargaError('');
    setExercises(data ?? []);
    setExLoading(false);
  }

  async function loadAssignments() {
    setAssLoading(true);
    // [BUG 9] La FK va NOMBRADA y no se puede volver a abreviar. `user_credits`
    // tiene DOS claves ajenas a `profiles` —`user_id`, el alumno del bono, y
    // `granted_by`, quien lo regaló—, así que con `profiles!inner(...)` a secas
    // PostgREST no elige: responde 300 PGRST201 y la consulta no devuelve NADA.
    // Aquí manda `user_id`, que es el alumno al que se le asigna entrenador.
    //
    // La segunda FK no la trajo ningún cambio de esta página: la mig 048 quiso
    // crearla y en producción no se creó (su `ADD COLUMN IF NOT EXISTS` se come
    // el `REFERENCES` cuando la columna ya existe) y la mig 223 la creó al
    // alinear el ledger. Desde entonces esta pantalla salía vacía; no se notó
    // hasta que hubo un bono GYM activo que listar.
    const [{ data: ass, error: assError }, { data: mons, error: monsError }] = await Promise.all([
      supabase
        .from('user_credits')
        .select('user_id, expiry_date, profiles!user_id!inner(full_name, email)')
        .eq('credit_type', 'gym')
        .eq('active', true),
      // ⚠️ DIRECCIÓN TAMBIÉN PUEDE LLEVAR PREPARACIÓN FÍSICA. Se ensancha el
      // ROL, no el requisito: el flag `is_physical_trainer` sigue mandando, así
      // que hoy esto no cambia nada —ningún director lo tiene puesto, medido—.
      // Entra para que el día que se lo pongas funcione; si no, el toggle se
      // activaría y el desplegable seguiría sin ofrecerlo, que es la clase de
      // fallo que nadie relaciona con nada.
      supabase
        .from('profiles')
        .select('id, full_name, role')
        .in('role', ROLES_ASIGNABLES)
        .eq('is_physical_trainer', true)
        .order('full_name'),
    ]);

    // Una lectura que falla NO es una lista vacía. Sin esto, el 300 dejaba
    // `data` en null y la pantalla decía que no hay bonos GYM que asignar —
    // con el bono ahí, recién regalado.
    if (assError || monsError) {
      setCargaError(`No se pudieron cargar las asignaciones: ${(assError ?? monsError)?.message}`);
      setAssignments([]);
      setAssLoading(false);
      return;
    }
    setCargaError('');

    const credits = (ass ?? []) as unknown as RawAssignmentCreditRow[];
    const userIds = credits.map((a) => a.user_id);
    const { data: currentAssignmentsRaw, error: errAssigns } = await supabase
      .from('gym_monitor_assignments')
      .select(
        'id, user_id, monitor_id, assigned_at, active, profiles!gym_monitor_assignments_monitor_id_fkey(full_name)'
      )
      .in('user_id', userIds.length ? userIds : ['00000000-0000-0000-0000-000000000000'])
      .eq('active', true);

    // ⚠️ Esta es la lectura que dice QUÉ ENTRENADOR tiene ya cada alumno, y
    //    esta es la pantalla desde la que se asigna. Sin ella todos salen sin
    //    entrenador, y asignar uno a quien ya lo tiene no es inocuo: solo
    //    puede haber una asignación activa por alumno (índice único parcial),
    //    y es la asignación la que activa el bono y le arranca la validez.
    if (errAssigns) {
      setCargaError(
        'No se ha podido leer qué entrenador tiene asignado cada alumno. No asignes nada con esta lista delante: los que salgan sin entrenador pueden tenerlo.'
      );
      setAssLoading(false);
      return;
    }

    const currentAssignments = (currentAssignmentsRaw ??
      []) as unknown as RawCurrentAssignmentRow[];
    const assignmentByUser: Record<string, RawCurrentAssignmentRow> = {};
    currentAssignments.forEach((a) => {
      assignmentByUser[a.user_id] = a;
    });

    const merged: Assignment[] = credits.map((a) => ({
      id: assignmentByUser[a.user_id]?.id ?? '',
      user_id: a.user_id,
      monitor_id: assignmentByUser[a.user_id]?.monitor_id ?? null,
      assigned_at: assignmentByUser[a.user_id]?.assigned_at ?? '',
      active: true,
      user: a.profiles,
      monitor: assignmentByUser[a.user_id]?.profiles ?? undefined,
      expiry_date: a.expiry_date,
    }));

    setAssignments(merged);
    setMonitors(mons ?? []);
    setAssLoading(false);
  }

  async function loadHistory() {
    setHistoryLoading(true);
    // [BUG 9] FK nombrada, mismo motivo que en `loadAssignments`.
    const { data: creditsRaw, error: histError } = await supabase
      .from('user_credits')
      .select(
        'id, user_id, purchase_date, activated_at, expiry_date, active, is_gift, profiles!user_id!inner(full_name, email)'
      )
      .eq('credit_type', 'gym')
      .order('purchase_date', { ascending: false });
    if (histError) {
      setCargaError(`No se pudo cargar el histórico de bonos: ${histError.message}`);
      setHistory([]);
      setHistoryLoading(false);
      return;
    }
    const credits = (creditsRaw ?? []) as unknown as RawHistoryCreditRow[];

    const userIds = Array.from(new Set(credits.map((c) => c.user_id)));
    const { data: monitorAssignsRaw, error: errHistAssigns } = await supabase
      .from('gym_monitor_assignments')
      .select(
        'user_id, assigned_at, active, profiles!gym_monitor_assignments_monitor_id_fkey(full_name)'
      )
      .in('user_id', userIds.length ? userIds : ['00000000-0000-0000-0000-000000000000'])
      .order('assigned_at', { ascending: false });
    // En el historial el entrenador cae a «—» y eso ya se lee como que no
    // constaba. Se dice que la lectura falló en vez de dejarlo así.
    if (errHistAssigns) {
      setCargaError(
        'No se ha podido leer el histórico de entrenadores asignados. Los guiones de esa columna no significan que no hubiera ninguno.'
      );
    }
    const monitorAssigns = (monitorAssignsRaw ?? []) as unknown as RawMonitorAssignRow[];

    // Per user, take the most recent assignment (active or not) as the displayed monitor.
    // gym_monitor_assignments no enlaza a un user_credit concreto, así que no hay forma
    // estricta de saber qué monitor estuvo asociado a cada bono histórico.
    const monitorByUser: Record<string, RawMonitorAssignRow> = {};
    monitorAssigns.forEach((m) => {
      if (!monitorByUser[m.user_id]) monitorByUser[m.user_id] = m;
    });

    const merged: HistoryCredit[] = credits.map((c) => ({
      id: c.id,
      user_id: c.user_id,
      purchase_date: c.purchase_date,
      activated_at: c.activated_at,
      expiry_date: c.expiry_date,
      active: c.active,
      is_gift: c.is_gift,
      user: c.profiles,
      monitor: monitorByUser[c.user_id]?.profiles ?? null,
    }));

    setHistory(merged);
    setHistoryLoading(false);
  }

  async function loadDashboard() {
    const weekAgo = new Date();
    weekAgo.setDate(weekAgo.getDate() - 7);

    const [activeRes, sessionsRes, videosRes] = await Promise.all([
      supabase
        .from('user_credits')
        .select('*', { count: 'exact', head: true })
        .eq('credit_type', 'gym')
        .eq('active', true),
      supabase
        .from('gym_sessions')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'completed')
        .gte('started_at', weekAgo.toISOString()),
      supabase
        .from('gym_videos')
        .select('*', { count: 'exact', head: true })
        .eq('feedback_status', 'pendiente'),
    ]);

    setDashStats({
      activeStudents: activeRes.error ? null : (activeRes.count ?? 0),
      weekSessions: sessionsRes.error ? null : (sessionsRes.count ?? 0),
      pendingVideos: videosRes.error ? null : (videosRes.count ?? 0),
    });
  }

  async function handleSaveExercise() {
    setExSaving(true);
    const payload = {
      ...exForm,
      video_url: exForm.video_url || null,
      exercisedb_gif_url: exForm.exercisedb_gif_url || null,
      description: exForm.description || null,
    };
    setExModalError('');
    const { error } = exEditTarget
      ? await supabase.from('gym_exercises').update(payload).eq('id', exEditTarget.id)
      : await supabase
          .from('gym_exercises')
          .insert({ ...payload, primary_muscles: [], secondary_muscles: [] });
    if (error) {
      setExModalError(`No se pudo guardar el ejercicio: ${error.message}`);
      setExSaving(false);
      return;
    }
    await loadExercises();
    setExModal(false);
    setExSaving(false);
  }

  async function assignMonitor(userId: string, monitorId: string | null) {
    setAsignError('');
    // Desactivar asignación actual
    const { error: bajaError } = await supabase
      .from('gym_monitor_assignments')
      .update({ active: false })
      .eq('user_id', userId)
      .eq('active', true);
    if (bajaError) {
      setAsignError(`No se pudo cambiar el entrenador: ${bajaError.message}`);
      return;
    }

    if (monitorId) {
      const { error: altaError } = await supabase.from('gym_monitor_assignments').insert({
        user_id: userId,
        monitor_id: monitorId,
        assigned_by: (await supabase.auth.getUser()).data.user?.id,
        active: true,
      });
      // La asignación vieja YA está desactivada. Si esta no entra, el alumno
      // se queda sin entrenador: hay que decirlo con esas palabras.
      if (altaError) {
        setAsignError(
          `El alumno se ha quedado SIN entrenador de gimnasio: ${altaError.message}. Vuelve a asignarlo.`
        );
        await loadAssignments();
        return;
      }
    }
    await loadAssignments();
  }

  const filteredExercises = exercises.filter((e) => {
    const matchSearch =
      e.name_es.toLowerCase().includes(exSearch.toLowerCase()) ||
      e.name_en.toLowerCase().includes(exSearch.toLowerCase());
    const matchCat = exCategory === 'todos' || e.category === exCategory;
    return matchSearch && matchCat;
  });

  return (
    <div className="space-y-6">
      {cargaError && (
        <p
          className="text-error text-sm border border-error/30 rounded px-3 py-2"
          data-testid="gym-carga-error"
        >
          {cargaError}
        </p>
      )}
      {asignError && (
        <p
          className="text-error text-sm border border-error/30 rounded px-3 py-2"
          data-testid="gym-asignacion-error"
        >
          {asignError}
        </p>
      )}
      {/* Tabs */}
      <div className="flex gap-3">
        <button
          onClick={() => setTab('exercises')}
          className={tab === 'exercises' ? 'btn-gold' : 'btn-ghost'}
        >
          <Dumbbell size={15} className="inline mr-2" />
          Ejercicios
        </button>
        <button
          onClick={() => setTab('assignments')}
          className={tab === 'assignments' ? 'btn-gold' : 'btn-ghost'}
        >
          <Users size={15} className="inline mr-2" />
          Asignaciones
        </button>
        <button
          onClick={() => setTab('history')}
          className={tab === 'history' ? 'btn-gold' : 'btn-ghost'}
        >
          <History size={15} className="inline mr-2" />
          Histórico
        </button>
        <button
          onClick={() => setTab('dashboard')}
          className={tab === 'dashboard' ? 'btn-gold' : 'btn-ghost'}
        >
          <BarChart2 size={15} className="inline mr-2" />
          Dashboard
        </button>
      </div>

      {/* ---- EJERCICIOS ---- */}
      {tab === 'exercises' && (
        <>
          <div className="flex flex-wrap gap-3 items-center justify-between">
            <div className="flex gap-3 items-center flex-wrap">
              <div className="relative w-64">
                <Search
                  size={16}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary"
                />
                <input
                  className="input pl-9 h-9 text-sm"
                  placeholder="Buscar ejercicio..."
                  value={exSearch}
                  onChange={(e) => setExSearch(e.target.value)}
                />
              </div>
              <select
                className="input h-9 text-sm w-44"
                value={exCategory}
                onChange={(e) => setExCategory(e.target.value)}
              >
                <option value="todos">Todas las categorías</option>
                {Object.entries(CATEGORY_LABELS).map(([k, v]) => (
                  <option key={k} value={k}>
                    {v}
                  </option>
                ))}
              </select>
            </div>
            <button
              onClick={() => {
                setExEditTarget(null);
                setExForm(EXERCISE_FORM_EMPTY);
                setExModal(true);
              }}
              className="btn-gold flex items-center gap-2"
            >
              <Plus size={16} /> Nuevo ejercicio
            </button>
          </div>

          <div className="card p-0 overflow-hidden">
            {exLoading ? (
              <div className="flex items-center justify-center h-48">
                <div className="w-8 h-8 border-2 border-gold-primary/30 border-t-gold-primary rounded-full animate-spin" />
              </div>
            ) : (
              <table className="w-full">
                <thead className="border-b border-bg-border bg-bg-elevated">
                  <tr>
                    <th className="table-th">Nombre ES</th>
                    <th className="table-th">Categoría</th>
                    <th className="table-th">Material</th>
                    <th className="table-th">Dif.</th>
                    <th className="table-th">Medición</th>
                    <th className="table-th">YouTube</th>
                    <th className="table-th">GIF</th>
                    <th className="table-th">Estado</th>
                    <th className="table-th"></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredExercises.map((e) => (
                    <tr key={e.id} className={`table-row ${!e.active ? 'opacity-50' : ''}`}>
                      <td className="table-td">
                        <p className="font-medium text-sm">{e.name_en}</p>
                        <p className="text-text-secondary text-xs">{e.name_es}</p>
                      </td>
                      <td className="table-td text-xs">
                        {CATEGORY_LABELS[e.category] ?? e.category}
                      </td>
                      <td className="table-td text-xs text-text-secondary">
                        {MATERIAL_LABELS[e.material] ?? e.material}
                      </td>
                      <td className="table-td">
                        <span className="font-oswald text-gold-primary">{e.difficulty}/5</span>
                      </td>
                      <td className="table-td text-xs text-text-secondary">
                        {MEASUREMENT_LABELS[e.measurement_type] ?? e.measurement_type}
                      </td>
                      <td className="table-td">
                        {e.video_url ? (
                          <span className="badge-success text-xs">Sí</span>
                        ) : (
                          <span className="badge-error text-xs">No</span>
                        )}
                      </td>
                      <td className="table-td">
                        {e.exercisedb_gif_url ? (
                          <span className="badge-success text-xs">Sí</span>
                        ) : (
                          <span className="text-text-secondary text-xs">—</span>
                        )}
                      </td>
                      <td className="table-td">
                        <span className={e.active ? 'badge-success' : 'badge-error'}>
                          {e.active ? 'Activo' : 'Inactivo'}
                        </span>
                      </td>
                      <td className="table-td">
                        <button
                          onClick={() => {
                            setExEditTarget(e);
                            setExForm({
                              name_es: e.name_es,
                              name_en: e.name_en,
                              description: e.description ?? '',
                              category: e.category,
                              material: e.material,
                              difficulty: e.difficulty,
                              measurement_type: e.measurement_type,
                              video_url: e.video_url ?? '',
                              exercisedb_gif_url: e.exercisedb_gif_url ?? '',
                              consult_trainer: e.consult_trainer,
                              active: e.active,
                            });
                            setExModal(true);
                          }}
                          className="text-text-secondary hover:text-gold-primary"
                        >
                          <Edit size={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          <p className="text-text-secondary text-xs">
            {filteredExercises.length} de {exercises.length} ejercicios
          </p>
        </>
      )}

      {/* ---- ASIGNACIONES ---- */}
      {tab === 'assignments' && (
        <>
          {monitors.length === 0 && (
            <div className="p-4 rounded-lg bg-bg-elevated border border-bg-border text-sm text-text-secondary">
              Nadie tiene activado el toggle de preparador físico. Ve a{' '}
              <strong className="text-white">Entrenadores</strong> y actívalo en quien lo lleve —
              puede ser un entrenador o alguien de dirección.
            </div>
          )}
          <div className="card p-0 overflow-hidden">
            {assLoading ? (
              <div className="flex items-center justify-center h-48">
                <div className="w-8 h-8 border-2 border-gold-primary/30 border-t-gold-primary rounded-full animate-spin" />
              </div>
            ) : (
              <table className="w-full">
                <thead className="border-b border-bg-border bg-bg-elevated">
                  <tr>
                    <th className="table-th">Alumno</th>
                    <th className="table-th">Caduca bono PF</th>
                    <th className="table-th">Monitor asignado</th>
                  </tr>
                </thead>
                <tbody>
                  {assignments.length === 0 ? (
                    <tr>
                      <td colSpan={3} className="table-td text-center text-text-secondary py-12">
                        No hay alumnos con suscripción Preparación Física activa
                      </td>
                    </tr>
                  ) : (
                    assignments.map((a) => (
                      <tr key={a.user_id} className="table-row">
                        <td className="table-td">
                          <p className="font-medium">{a.user?.full_name}</p>
                          <p className="text-text-secondary text-xs">{a.user?.email}</p>
                        </td>
                        <td className="table-td text-text-secondary text-sm">
                          {a.expiry_date ? format(new Date(a.expiry_date), 'dd/MM/yyyy') : '—'}
                        </td>
                        <td className="table-td">
                          <select
                            className="input h-10 text-sm w-64"
                            value={a.monitor_id ?? ''}
                            onChange={(e) => assignMonitor(a.user_id, e.target.value || null)}
                          >
                            <option value="">Sin asignar</option>
                            <OpcionesDeEntrenador lista={monitors} />
                          </select>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}

      {/* ---- HISTÓRICO ---- */}
      {tab === 'history' && (
        <>
          <div className="flex flex-wrap gap-3 items-center justify-between">
            <select
              className="input h-9 text-sm w-56"
              value={historyFilter}
              onChange={(e) => setHistoryFilter(e.target.value as CreditStatus | 'todos')}
            >
              <option value="todos">Todos los estados</option>
              <option value="activo">Activos</option>
              <option value="pendiente">Pendientes de activar</option>
              <option value="agotado">Agotados</option>
            </select>
            <p className="text-text-secondary text-xs">
              {
                history.filter(
                  (c) => historyFilter === 'todos' || computeStatus(c) === historyFilter
                ).length
              }{' '}
              de {history.length} bonos
            </p>
          </div>

          <div className="card p-0 overflow-hidden">
            {historyLoading ? (
              <div className="flex items-center justify-center h-48">
                <div className="w-8 h-8 border-2 border-gold-primary/30 border-t-gold-primary rounded-full animate-spin" />
              </div>
            ) : (
              <table className="w-full">
                <thead className="border-b border-bg-border bg-bg-elevated">
                  <tr>
                    <th className="table-th">Alumno</th>
                    <th className="table-th">Monitor</th>
                    <th className="table-th">Compra</th>
                    <th className="table-th">Activación</th>
                    <th className="table-th">Caducidad</th>
                    <th className="table-th">Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {history.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="table-td text-center text-text-secondary py-12">
                        Sin bonos Preparación Física registrados
                      </td>
                    </tr>
                  ) : (
                    history
                      .filter(
                        (c) => historyFilter === 'todos' || computeStatus(c) === historyFilter
                      )
                      .map((c) => {
                        const status = computeStatus(c);
                        const badge = STATUS_BADGE[status];
                        return (
                          <tr key={c.id} className="table-row">
                            <td className="table-td">
                              <p className="font-medium">{c.user?.full_name}</p>
                              <p className="text-text-secondary text-xs">{c.user?.email}</p>
                              {c.is_gift && (
                                <p className="text-text-secondary text-xs mt-0.5">🎁 Regalo</p>
                              )}
                            </td>
                            <td className="table-td text-sm">
                              {c.monitor?.full_name ?? (
                                <span className="text-text-secondary">—</span>
                              )}
                            </td>
                            <td className="table-td text-text-secondary text-sm">
                              {format(new Date(c.purchase_date), 'dd/MM/yyyy')}
                            </td>
                            <td className="table-td text-text-secondary text-sm">
                              {c.activated_at
                                ? format(new Date(c.activated_at), 'dd/MM/yyyy')
                                : '—'}
                            </td>
                            <td className="table-td text-text-secondary text-sm">
                              {c.expiry_date ? format(new Date(c.expiry_date), 'dd/MM/yyyy') : '—'}
                            </td>
                            <td className="table-td">
                              <span className={badge.cls}>{badge.label}</span>
                            </td>
                          </tr>
                        );
                      })
                  )}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}

      {/* ---- DASHBOARD ---- */}
      {tab === 'dashboard' && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="card text-center">
            <p
              className={`font-oswald text-4xl ${dashStats.activeStudents === null ? 'text-error' : 'text-green-400'}`}
              data-testid="gym-dash-alumnos"
              title={
                dashStats.activeStudents === null
                  ? 'Sin comprobar: la consulta ha fallado'
                  : undefined
              }
            >
              {dashStats.activeStudents ?? '—'}
            </p>
            <p className="text-text-secondary mt-1 text-sm">
              Alumnos con suscripción Preparación Física activa
            </p>
          </div>
          <div className="card text-center">
            <p
              className={`font-oswald text-4xl ${dashStats.weekSessions === null ? 'text-error' : 'text-gold-primary'}`}
              data-testid="gym-dash-sesiones"
              title={
                dashStats.weekSessions === null
                  ? 'Sin comprobar: la consulta ha fallado'
                  : undefined
              }
            >
              {dashStats.weekSessions ?? '—'}
            </p>
            <p className="text-text-secondary mt-1 text-sm">Sesiones completadas esta semana</p>
          </div>
          <div className="card text-center">
            <p
              className="font-oswald text-4xl text-error"
              data-testid="gym-dash-videos"
              title={
                dashStats.pendingVideos === null
                  ? 'Sin comprobar: la consulta ha fallado'
                  : undefined
              }
            >
              {dashStats.pendingVideos ?? '—'}
            </p>
            <p className="text-text-secondary mt-1 text-sm">Vídeos pendientes de revisión</p>
          </div>
        </div>
      )}

      {/* Modal ejercicio */}
      <Modal
        open={exModal}
        onClose={() => setExModal(false)}
        title={exEditTarget ? 'Editar ejercicio' : 'Nuevo ejercicio'}
      >
        <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">Nombre ES *</label>
              <input
                className="input"
                value={exForm.name_es}
                onChange={(e) => setExForm((f) => ({ ...f, name_es: e.target.value }))}
              />
            </div>
            <div>
              <label className="label">Nombre EN *</label>
              <input
                className="input"
                value={exForm.name_en}
                onChange={(e) => setExForm((f) => ({ ...f, name_en: e.target.value }))}
              />
            </div>
          </div>
          <div>
            <label className="label">Descripción técnica</label>
            <textarea
              className="input resize-none"
              rows={3}
              value={exForm.description}
              onChange={(e) => setExForm((f) => ({ ...f, description: e.target.value }))}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">Categoría</label>
              <select
                className="input"
                value={exForm.category}
                onChange={(e) => setExForm((f) => ({ ...f, category: e.target.value }))}
              >
                {Object.entries(CATEGORY_LABELS).map(([k, v]) => (
                  <option key={k} value={k}>
                    {v}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="label">Material</label>
              <select
                className="input"
                value={exForm.material}
                onChange={(e) => setExForm((f) => ({ ...f, material: e.target.value }))}
              >
                {Object.entries(MATERIAL_LABELS).map(([k, v]) => (
                  <option key={k} value={k}>
                    {v}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="label">Tipo de medición</label>
              <select
                className="input"
                value={exForm.measurement_type}
                onChange={(e) => setExForm((f) => ({ ...f, measurement_type: e.target.value }))}
              >
                {Object.entries(MEASUREMENT_LABELS).map(([k, v]) => (
                  <option key={k} value={k}>
                    {v}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="label">Dificultad (1-5)</label>
              <input
                type="number"
                min={1}
                max={5}
                className="input"
                value={exForm.difficulty}
                onChange={(e) => setExForm((f) => ({ ...f, difficulty: +e.target.value }))}
              />
            </div>
          </div>
          <div className="grid grid-cols-1 gap-3 p-3 rounded-lg border border-bg-border bg-bg-elevated">
            <p className="text-xs font-medium text-text-secondary uppercase tracking-wide">
              Referencias visuales (el alumno ve YouTube si existe, si no el GIF)
            </p>
            <div>
              <label className="label">URL vídeo YouTube</label>
              <input
                className="input"
                placeholder="https://youtube.com/watch?v=..."
                value={exForm.video_url}
                onChange={(e) => setExForm((f) => ({ ...f, video_url: e.target.value }))}
              />
            </div>
            <div>
              <label className="label">
                GIF ExerciseDB{' '}
                <span className="text-text-secondary font-normal">
                  (fallback si no hay YouTube)
                </span>
              </label>
              <input
                className="input"
                placeholder="https://static.exercisedb.dev/media/..."
                value={exForm.exercisedb_gif_url}
                onChange={(e) => setExForm((f) => ({ ...f, exercisedb_gif_url: e.target.value }))}
              />
              {exForm.exercisedb_gif_url && (
                <img
                  src={exForm.exercisedb_gif_url}
                  alt="preview"
                  className="mt-2 h-28 rounded-md object-contain bg-black/30"
                />
              )}
            </div>
          </div>
          <div className="flex gap-6">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={exForm.consult_trainer}
                onChange={(e) => setExForm((f) => ({ ...f, consult_trainer: e.target.checked }))}
                className="w-4 h-4 accent-gold-primary"
              />
              <span className="text-sm text-white">Consultar con monitor</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={exForm.active}
                onChange={(e) => setExForm((f) => ({ ...f, active: e.target.checked }))}
                className="w-4 h-4 accent-gold-primary"
              />
              <span className="text-sm text-white">Activo</span>
            </label>
          </div>
          {exModalError && (
            <p className="text-error text-sm" data-testid="gym-ejercicio-modal-error">
              {exModalError}
            </p>
          )}
          <div className="flex justify-end gap-3">
            <button onClick={() => setExModal(false)} className="btn-ghost">
              Cancelar
            </button>
            <button onClick={handleSaveExercise} disabled={exSaving} className="btn-gold">
              {exSaving ? 'Guardando...' : 'Guardar'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
